import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import LocationsWeServe from '../components/LocationsWeServe';
import LocalSingles from '../components/LocalSingles';

const NEIGHBORHOODS = [
  {
    name: 'Downtown Orlando',
    text: 'Fast schedules, full calendars, a social scene that keeps moving. Find someone who enjoys the pace, not just the postcode.',
  },
  {
    name: 'Winter Park',
    text: 'A good conversation carries more weight than a crowded room here. Meet people whose interests give you something real to talk about.',
  },
  {
    name: 'Lake Nona',
    text: 'New routines, ambitious careers, evolving communities. The better question is who fits naturally into the life you’re building.',
  },
];

const STEPS = [
  {
    title: 'Meet',
    text: 'Create your profile and discover Orlando singles who share more than your location.',
  },
  {
    title: 'Mingle',
    text: 'Explore interests, communicate openly, and uncover what you have in common.',
  },
  {
    title: 'Understand',
    text: 'Get clearer insight into mutual interest and compatibility. Love Diamond and Interests let you monitor and express interest in real time, so you know where you stand instead of guessing.',
  },
];

const FAQS = [
  {
    q: 'What is the best dating site Orlando has to offer?',
    a: 'SLAMMMM takes a thoughtful approach to matching. There’s no single Orlando dating site that’s best for everyone; look for genuine connections, compatibility, ease of use, and relationship goals.',
  },
  {
    q: 'Is there a free dating site Orlando FL residents can actually use?',
    a: 'Yes. SLAMMMM is a free dating site Orlando, FL daters can join at no cost. As an Orlando free dating site, you create your profile and start exploring connections before spending anything.',
  },
  {
    q: 'What dating services in Orlando does SLAMMMM offer?',
    a: 'SLAMMMM offers profile creation, local matching, and tools like Love Diamond and Interests for a clearer read on mutual interest, all as part of a free dating service in Orlando.',
  },
  {
    q: 'How can I meet singles in Orlando?',
    a: 'SLAMMMM connects the two key pieces: meeting someone nearby online, and local events and social spaces to meet offline. Online dating widens your circle beyond who you already know.',
  },
  {
    q: 'Is online dating in Orlando safe?',
    a: 'It can be, when you set the pace. Protect personal information, take conversations slow, and choose a public place for a first meeting. SLAMMMM’s available safety tools are there when you need them.',
  },
  {
    q: 'Does SLAMMMM work for Christian singles in Orlando?',
    a: 'SLAMMMM doesn’t filter by religion. Singles searching for Christian singles in Orlando are welcome to build a profile around their own values and what they’re looking for. Compatibility is based on shared interests, lifestyle, and goals, whatever those may be.',
  },
];

const FAQ_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((item) => ({
    '@type': 'Question',
    name: item.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.a,
    },
  })),
});

const SERVICE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': 'https://www.slammmm.com/orlando#service',
  name: 'Online Dating Service in Orlando',
  description: 'Online dating service helping singles in Orlando discover local connections based on shared interests, values, lifestyle, and relationship goals.',
  url: 'https://www.slammmm.com/orlando',
  provider: {
    '@type': 'Organization',
    name: 'SLAMMMM',
    url: 'https://www.slammmm.com/',
  },
  serviceType: 'Online Dating Service',
  category: 'Dating Services',
  areaServed: {
    '@type': 'City',
    name: 'Orlando',
    containedInPlace: {
      '@type': 'State',
      name: 'Florida',
      containedInPlace: {
        '@type': 'Country',
        name: 'United States',
      },
    },
  },
});

const Orlando: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Seo
        title="Dating in Orlando, FL | Meet Singles on SLAMMMM"
        description="Meet singles in Orlando, including single women and Christian singles. Join SLAMMMM for free and discover a different way to connect through online dating."
        schemas={[FAQ_SCHEMA, SERVICE_SCHEMA]}
      />

      {/* Hero */}
      <section className="py-100 breadcrumb_section bg-light border-bottom">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto text-center">
              <div className="section_title">
                <span className="text-primary d-block font500 mb-2">Dating in Orlando, FL | Meet Local Singles for Free</span>
                <h1 className="mb-4">Dating in Orlando, Florida | Meet Orlando Singles Online</h1>
              </div>
              <p className="font20 mb-3">
                SLAMMMM brings a smarter approach to dating in Orlando, Florida. Dating in Orlando can mean meeting someone new almost anywhere, but the real question isn&rsquo;t how many people you meet. It&rsquo;s which connection is actually worth exploring.
              </p>
              <p className="font20 mb-4">
                SLAMMMM helps you look past the first impression and discover what fits.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Meet Orlando Singles Free</button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Attraction vs alignment */}
      <section className="py-100 our_values_section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-5">
              <div className="section_title">
                <h2 className="mb-0">Where Attraction Meets True Alignment in Orlando</h2>
              </div>
            </div>
            <div className="col-lg-7">
              <p className="font20 mb-3">
                Attraction gets your attention. Alignment gives the connection somewhere to go. Dating in Orlando gives you plenty of chances to meet someone new.
              </p>
              <p className="font20 mb-0">
                <Link to="/">SLAMMMM</Link> looks deeper at interests, values, communication, lifestyle, and relationship goals. The things that make two people click beyond the first spark.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond geography */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">Beyond Geography: Finding the Connection That Actually Fits</h2>
            </div>
            <p className="font20 mb-0 mt-3">
              Orlando tells you where someone is. It doesn&rsquo;t tell you how well you fit.
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="card h-100">
                <h3 className="font28 mb-3 pb-3 border-bottom">More Than a Profile</h3>
                <p className="mb-0 font20">
                  A photo catches your eye. A profile starts the conversation. The real story is in the details: interests, values, lifestyle, communication style, and what you both want.
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card h-100">
                <h3 className="font28 mb-3 pb-3 border-bottom">Less Guessing. More Understanding.</h3>
                <p className="mb-0 font20">
                  SLAMMMM brings those details into the dating experience. More to go on than attraction or location alone. Less guessing. Less wasted time. Better-informed connections.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Join Free Today</button></Link>
          </div>
        </div>
      </section>

      {/* Orlando single women / men */}
      <LocalSingles
        city="Orlando"
        womenHeading="Orlando Single Women"
        womenText="SLAMMMM makes it easy to connect with Orlando single women. Search for single women in Orlando, Florida, or single ladies in Orlando, Florida, and look past the profile picture. Shared interests, values, and relationship goals matter more than a photo."
        menText="Looking to meet single men in Orlando? Go beyond the profile and discover what you actually have in common, from everyday interests to bigger relationship goals."
      />

      {/* Neighborhoods */}
      <section className="py-100 our_values_section bg-light">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title">
              <h2 className="mb-0">A Smarter Way to Connect Across Orlando&rsquo;s Neighborhoods</h2>
            </div>
            <p className="font20 mb-0 mt-3">
              Orlando puts you in the same zip code. Your way of living is what makes a connection click. SLAMMMM helps you explore nearby singles without letting geography do all the choosing.
            </p>
          </div>

          <div className="row g-4">
            {NEIGHBORHOODS.map((n) => (
              <div className="col-md-4" key={n.name}>
                <div className="card h-100">
                  <h3 className="font22 mb-3 pb-2 border-bottom text-primary">{n.name}</h3>
                  <p className="mb-0 font20">{n.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-100 our_approach_section">
        <div className="container">
          <div className="border-bottom pb-5 mb-4">
            <div className="section_title text-center">
              <h2 className="mb-0">How Orlando Dating Works on SLAMMMM</h2>
            </div>
            <p className="font20 mb-0 mt-3 text-center">
              Orlando singles dating doesn&rsquo;t have to feel like guesswork. SLAMMMM turns meeting someone new into understanding whether the connection actually works.
            </p>
          </div>

          <div className="row g-4">
            {STEPS.map((step, i) => (
              <div className="col-md-4" key={step.title}>
                <div className="card h-100">
                  <div className="d-flex justify-content-between align-items-center border-bottom mb-3 pb-2">
                    <h3 className="font28 mb-0">{step.title}</h3>
                    <p className="font28 mb-0 text-primary opacity-50">{`0${i + 1}`}</p>
                  </div>
                  <p className="mb-0 font20">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/signup"><button className="btn btn-primary">Start Dating in Orlando</button></Link>
          </div>
        </div>
      </section>

      <LocationsWeServe />

      {/* Final CTA */}
      <section className="py-100 love_journey_section">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center z-3 position-relative">
              <div className="section_title mb-3">
                <h2 className="text-white">Ready to Meet Someone in Orlando?</h2>
              </div>
              <p className="text-white font20 mb-4 pb-2">
                SLAMMMM makes your next connection easier to find. Meet singles in Orlando and discover the compatibility that turns a simple match into something worth exploring.
              </p>
              <Link to="/signup"><button className="btn btn-primary">Sign Up Free, Meet Orlando Singles</button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-100 help_faq_section" id="orlando-faq">
        <div className="container">
          <div className="section_title text-center mb-5">
            <h2 className="mb-0">Dating in Orlando FAQ&rsquo;s</h2>
          </div>

          <div className="row">
            <div className="col-lg-9 mx-auto">
              <div className="accordion" id="orlando-faq-accordion">
                {FAQS.map((item, index) => (
                  <div className="accordion-item" key={item.q}>
                    <h3 className="accordion-header">
                      <button
                        className={`accordion-button ${index === 0 ? '' : 'collapsed'}`}
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target={`#orlando-faq-${index}`}
                        aria-expanded={index === 0 ? 'true' : 'false'}
                        aria-controls={`orlando-faq-${index}`}
                      >
                        {item.q}
                      </button>
                    </h3>
                    <div
                      id={`orlando-faq-${index}`}
                      className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                      data-bs-parent="#orlando-faq-accordion"
                    >
                      <div className="accordion-body">{item.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Orlando;
